import { ContainerClient } from '@azure/storage-blob';
import { Box, Flex, IconButton, Input, Spinner, Text, Tooltip, useToast } from '@chakra-ui/react';
import { translationAction, uploadAction } from 'actions';
import { Promise as BluebirdPromise } from 'bluebird';
import InputSearch from 'components/InputSearch';
import { FILE_EXTENSION_UPLOAD_ALLOWED } from 'containers/Upload/UploadInput';
import { MAX_CONCURRENT_REQUEST, MAXIMUM_SUMMARY_FILES_SELECT, TEMPORARY } from 'helpers/constant';
import { TRANSLATED_FILE_STATUS, TRANSLATION_FILE_STATUS, UPLOAD_FILES_STATUS } from 'helpers/enum';
import {
  formatBytes,
  formatBytestoKBytes,
  getFileExtensionFromFileName,
  removeQueryString,
} from 'helpers/file';
import { Check, FileText, Paperclip, X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentService } from 'services/document/document.service';
import { FileStore } from 'types/actions/upload';
import { FileDocument } from 'types/reducers/document';
import { TranslationFileDocument } from 'types/reducers/translation';
import { getBlobContainer } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

interface SelectFileProps {
  onOpenFile: (_file: FileDocument) => void;
}

// Module-level in-flight upload guard — survives component remounts (React StrictMode) and
// multiple mounted instances, unlike a useRef. Ensures one file set is uploaded exactly once
// (the effect can fire twice → 2 blob PUTs + 2 completeUploadFile → duplicate docs). Keyed on
// the file signature, cleared when the upload settles so legit re-uploads still work. #78306
const inFlightUploadSigs = new Set<string>();

const SelectFiles = ({ onOpenFile }: SelectFileProps) => {
  const { t } = useTranslation();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  // Guards the upload effect from adding the same files twice (it depends on [blobUrl,status,files]
  // and can re-fire for one upload, which previously duplicated every attached file). #77529-9
  const processedUploadRef = useRef<string>('');

  const toast = useToast();

  const dispatch = useDispatch();
  const { current } = useSelector((store) => store.translation);
  const { stats, status, files, blobUrl } = useSelector((state) => state.upload);
  const isTranslating = useSelector(
    (state) =>
      state.translation.current.results?.some(
        (file) => file.status === TRANSLATED_FILE_STATUS.PENDING
      )
  );

  const selectFilesCount = current.selectedFiles.length ?? 0;
  const isUploadingFiles = status === UPLOAD_FILES_STATUS.UPLOADING;
  const isPendingFileExist = current?.results?.some(
    (file) => file.status === TRANSLATED_FILE_STATUS.PENDING
  );
  const isDisableUpload = !current.task_id || isUploadingFiles || isTranslating;
  const isDisableSelect = Boolean(!current.task_id || isPendingFileExist || isTranslating);
  const isReachMaximumFilesSelect = selectFilesCount >= MAXIMUM_SUMMARY_FILES_SELECT;

  const searchFiles = async (value: string) => {
    return DocumentService.getListFiles({
      page: 0,
      size: 10,
      file_name: value,
      sort_field: 'created_at',
      is_ascending: 'false',
      status: 'PROCESSED',
    });
  };

  const showMaximumFileSelectWarning = () => {
    toast({
      title: `Maximum ${MAXIMUM_SUMMARY_FILES_SELECT} files selected!`,
      status: 'warning',
      position: 'bottom-right',
      isClosable: true,
    });
  };

  const onSelectFile = (newFile: FileDocument) => {
    if (isReachMaximumFilesSelect) {
      return showMaximumFileSelectWarning();
    }
    // Skip if already selected (prevents the same file appearing twice). #77529r2-5
    const alreadySelected = current.selectedFiles?.some(
      (f: TranslationFileDocument) => f.id === newFile.id || (!!f.name && f.name === newFile.name)
    );
    if (alreadySelected) return;

    dispatch(
      translationAction.addSelectedFile({
        ...newFile,
        translation_file_status: TRANSLATION_FILE_STATUS.UPDATED,
      })
    );
  };

  const onRemoveFile = (
    e: React.MouseEvent<SVGElement, MouseEvent>,
    deleteFile: TranslationFileDocument
  ) => {
    e.stopPropagation();
    // Block remove file when translating
    if (isTranslating) {
      return;
    }
    dispatch(translationAction.removeSelectedFile(deleteFile));
  };

  const handleUpload = () => {
    if (isUploadingFiles || isPendingFileExist) {
      return;
    }
    uploadInputRef.current?.click();
  };

  const onChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const remaining = MAXIMUM_SUMMARY_FILES_SELECT - selectFilesCount;
    if (remaining <= 0) {
      if (e.target) e.target.value = '';
      return showMaximumFileSelectWarning();
    }
    const incoming = Array.from((e.target?.files ?? []) as FileList);
    // Cap a multi-file pick to the remaining slots so the selection never exceeds the max (#77529-10)
    const files = incoming.slice(0, remaining);
    if (incoming.length > remaining) showMaximumFileSelectWarning();
    if (!FILE_EXTENSION_UPLOAD_ALLOWED.includes(getFileExtensionFromFileName(files[0]?.name))) {
      toast({
        title: t('upload.fileIsNotValid'),
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
      return;
    }
    processedUploadRef.current = ''; // allow the upcoming upload to be processed once
    dispatch(uploadAction.startUpload(files));
    if (e.target) e.target.value = ''; // reset so re-selecting the same file fires onChange again
  };

  const uploadFileToBlobStorage = async (containerClient: ContainerClient, file: File) => {
    const blobClient = containerClient.getBlockBlobClient(uuidv4() + '_' + file.name);

    const blobHTTPHeaders = { blobContentType: file.type };

    return blobClient
      .uploadData(file, {
        blockSize: 20 * 1024 * 1024,
        concurrency: 40,
        blobHTTPHeaders,
      })
      .then(() => {
        dispatch(uploadAction.uploadSuccess());
        return {
          url: removeQueryString(blobClient.url),
          name: file.name,
          size: formatBytestoKBytes(file.size, 2),
        };
      })
      .catch(() => {
        dispatch(uploadAction.uploadFail(file));
      });
  };

  useEffect(() => {
    if (status === UPLOAD_FILES_STATUS.UPLOADING && blobUrl && files && files?.length > 0) {
      // De-dupe: this effect re-fires for a single upload (blobUrl SAS-refresh + files settle
      // separately). Key the guard on the FILE SET only (NOT blobUrl) so one upload session
      // processes exactly once — otherwise a blobUrl change re-ran it → 2 "uploading"/"success"
      // toasts + double work for a single file. #78306
      const sig = files.map((f) => `${f.name}:${f.size}`).join(',');
      if (inFlightUploadSigs.has(sig)) return;
      inFlightUploadSigs.add(sig);
      const containerClient = getBlobContainer(blobUrl);
      for (const file of files) {
        // Skip files already selected (same name) — the signature guard only stops THIS effect
        // double-firing; re-uploading an already-selected file still duplicated it. #78082-4
        const alreadySelected = current.selectedFiles?.some(
          (f: TranslationFileDocument) => !!f.name && f.name === file.name
        );
        if (alreadySelected) continue;
        dispatch(
          translationAction.addSelectedFile({
            id: uuidv4(),
            name: file.name,
            url: '',
            size: parseFloat(formatBytestoKBytes(file.size, 2)),
            state: '',
            created_by: '',
            created_date: '',
            org: '',
            translation_file_status: TRANSLATION_FILE_STATUS.IDLE,
          })
        );
      }
      if (!toast.isActive('upload-start')) {
        toast({
          id: 'upload-start',
          title: t('upload.startUploading', { total: stats.total }),
          status: 'success',
          position: 'bottom-right',
          isClosable: true,
        });
      }

      const uploadedFile: FileStore[] = [];
      BluebirdPromise.map(
        files,
        async (file) => {
          return uploadFileToBlobStorage(containerClient, file).then((file) => {
            if (file) {
              uploadedFile.push(file);
            }
          });
        },
        { concurrency: MAX_CONCURRENT_REQUEST }
      )
        .then(() => {
          dispatch(
            uploadAction.completeUploadFile({
              files: uploadedFile,
              task_id: current.type !== TEMPORARY ? current.task_id : undefined,
            })
          );
          dispatch(
            translationAction.updateSelectedFile({
              data: {
                url: uploadedFile[0]?.url,
                translation_file_status: TRANSLATION_FILE_STATUS.UPLOADED,
              },
              isTemporary: current.type === TEMPORARY,
            })
          );
          dispatch(uploadAction.completeUpload());
        })
        .catch((e: Error) => console.log(e))
        .finally(() => {
          inFlightUploadSigs.delete(sig);
        });
    }
  }, [blobUrl, status, files]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status === UPLOAD_FILES_STATUS.FINISH) {
      if (uploadInputRef.current) uploadInputRef.current.value = '';
      if (stats.uploadSuccess > 0 && !toast.isActive('upload-success')) {
        toast({
          // isActive guard + id → skip if this toast is already shown (FINISH effect re-runs /
          // StrictMode remount). Chakra does NOT auto-dedupe by id, so the check is required. #78306
          id: 'upload-success',
          title: t('upload.uploadedSuccessfully', {
            uploadSuccess: stats.uploadSuccess,
            total: stats.total,
          }),
          status: 'success',
          position: 'bottom-right',
          isClosable: true,
        });
      }

      if (stats.uploadFail > 0) {
        toast({
          title: t('upload.uploadedFailed', { uploadFail: stats.uploadFail, total: stats.total }),
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
      }
      dispatch(uploadAction.resetUpload());
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    /* DS source-file picker pane — surface, border, ds_sm shadow (design-guidelines.md §4) */
    <Flex
      direction='column'
      overflow='hidden'
      h={'100%'}
      minH={0}
      bg='bg.surface'
      borderWidth='1px'
      borderColor='border.default'
      borderRadius='lg'
      shadow='ds_sm'
    >
      {/* Header: title + count badge + search/upload */}
      <Box flexShrink={0} px={4} pt={4} pb={3}>
        <Flex align='center' justify='space-between' mb={3}>
          <Text color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
            {t('translator.selectFiles')}
          </Text>
          <Flex
            align='center'
            h='22px'
            px={2}
            color={isReachMaximumFilesSelect ? 'state.warning' : 'primary.solid'}
            fontFamily='mono'
            fontSize='xs'
            fontWeight={600}
            bg={isReachMaximumFilesSelect ? 'state.warningSoft' : 'primary.soft'}
            borderRadius='sm'
          >
            {/* Show selected/max (X/5) to match Summary (#78762 follow-up) */}
            {selectFilesCount}/{MAXIMUM_SUMMARY_FILES_SELECT}
          </Flex>
        </Flex>

        <Flex gap={2} w='100%'>
          <Box flex='1' minW={0}>
            <InputSearch<FileDocument>
              callback={searchFiles}
              onSelect={onSelectFile}
              disabled={isDisableSelect}
            />
          </Box>
          <Tooltip
            label={isUploadingFiles ? t('translator.uploading') : t('translator.uploadFiles')}
          >
            <IconButton
              aria-label={t('translator.uploadFiles')}
              icon={isUploadingFiles ? <Spinner size='sm' /> : <Paperclip size={18} />}
              isDisabled={isDisableSelect}
              onClick={handleUpload}
              size='ds-sm'
              variant='ds-outline'
            />
          </Tooltip>
          <Input
            ref={uploadInputRef}
            display='none'
            accept={FILE_EXTENSION_UPLOAD_ALLOWED.join(', ')}
            isDisabled={isDisableUpload}
            onChange={onChangeFile}
            type='file'
          />
        </Flex>
      </Box>

      {/* Selected files list / empty state */}
      <Box flex={1} overflowY='auto' px={3} pb={3}>
        {selectFilesCount === 0 ? (
          <Text px={3} py={6} color='text.muted' fontSize='sm' textAlign='center'>
            {t('translator.selectFilesEmpty', {
              defaultValue: 'Upload or pick documents to translate.',
            })}
          </Text>
        ) : (
          <Flex direction='column' gap={2}>
            {current?.selectedFiles?.map((file) => {
              const isSaved = file.translation_file_status === TRANSLATION_FILE_STATUS.UPDATED;
              return (
                <Flex
                  key={file.id}
                  align='center'
                  gap={3}
                  px={3}
                  py={3}
                  bg='bg.surface'
                  borderWidth='1px'
                  borderColor={isSaved ? 'primary.solid' : 'border.default'}
                  borderRadius='md'
                  _hover={{ borderColor: 'primary.solid', shadow: 'ds_xs' }}
                  cursor='pointer'
                  transition='all .14s ease-out'
                  onClick={() => onOpenFile(file)}
                >
                  <Flex
                    align='center'
                    justify='center'
                    flexShrink={0}
                    w='32px'
                    h='32px'
                    color='primary.solid'
                    bg='primary.soft'
                    borderRadius='sm'
                  >
                    <FileText size={15} />
                  </Flex>
                  <Tooltip label={file.name} placement='top-start'>
                    <Box flex={1} minW={0}>
                      <Text
                        overflow='hidden'
                        color='text.default'
                        fontSize='sm'
                        fontWeight={500}
                        whiteSpace='nowrap'
                        textOverflow='ellipsis'
                      >
                        {file.name}
                      </Text>
                      <Text color='text.subtle' fontSize='xs'>
                        {formatBytes(file.size * 1024)}
                      </Text>
                    </Box>
                  </Tooltip>
                  {!isSaved && (
                    <Tooltip label={t('translator.uploading')}>
                      <Box flexShrink={0}>
                        <Spinner color='text.subtle' size='sm' />
                      </Box>
                    </Tooltip>
                  )}
                  {isSaved && (
                    <Tooltip label={t('translator.fileIsSaved')}>
                      <Box
                        flexShrink={0}
                        color='state.success'
                        aria-label={t('translator.fileIsSaved')}
                      >
                        <Check size={16} />
                      </Box>
                    </Tooltip>
                  )}
                  <Tooltip label={t('common.cancel')}>
                    <IconButton
                      flexShrink={0}
                      aria-label={t('common.cancel')}
                      icon={<X size={15} />}
                      onClick={(e) =>
                        onRemoveFile(e as unknown as React.MouseEvent<SVGElement, MouseEvent>, file)
                      }
                      size='ds-sm'
                      variant='ds-ghost'
                    />
                  </Tooltip>
                </Flex>
              );
            })}
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

export default SelectFiles;
